/**
 * An artifact as a file of the person's: its source as written, with the
 * extension its kind has, or exported to stand alone.
 *
 * A page or a component exported stands alone as one HTML file: the design
 * tokens set in its head, a component compiled (artifactReact.ts), and the
 * frame's own policy as a meta tag (`artifactPolicy`), so opened from disk
 * it reaches what it reached in the frame and nothing more. An SVG exported
 * carries its tokens in a style element of its own.
 */

import { artifactPolicy, type ArtifactKind } from '@wire';
import { withTokens } from './artifactFrame';

export interface ArtifactFile {
  name: string;
  type: string;
  body: string;
}

/** A file name from a title: no path separators or control characters. */
export function fileName(title: string, ext: string): string {
  const base = title.replace(/[\\/:*?"<>|\u0000-\u001f]+/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 80) || 'Artifact';
  return `${base}.${ext}`;
}

/** Whether a component's source reads as TypeScript: it is saved as .tsx. */
const typed = (source: string): boolean =>
  /\binterface\s+[A-Z]\w*\s*[{<]|\btype\s+[A-Z]\w*\s*=|:\s*(string|number|boolean|React\.\w+)\b|\bas\s+const\b/.test(source);

const SOURCE: Record<ArtifactKind, (content: string) => { ext: string; type: string }> = {
  html: () => ({ ext: 'html', type: 'text/html' }),
  svg: () => ({ ext: 'svg', type: 'image/svg+xml' }),
  markdown: () => ({ ext: 'md', type: 'text/markdown' }),
  quiz: () => ({ ext: 'json', type: 'application/json' }),
  react: (content) => ({ ext: typed(content) ? 'tsx' : 'jsx', type: 'text/plain' })
};

/** The source as written, named for its kind. */
export function sourceFile(kind: ArtifactKind, title: string, content: string): ArtifactFile {
  const { ext, type } = SOURCE[kind](content);
  return { name: fileName(title, ext), type, body: content };
}

/** Whether exporting gives more than the source: a page or a component
 *  always (its policy, and a component compiled), an SVG with tokens. */
export const exportable = (kind: ArtifactKind, tokens: string | null): boolean =>
  kind === 'html' || kind === 'react' || (kind === 'svg' && !!tokens);

const POLICY_META = `<meta http-equiv="Content-Security-Policy" content="${artifactPolicy().join('; ')}">`;

/** The frame's policy first in a document's head. */
function withPolicy(html: string): string {
  const head = /<head(\s[^>]*)?>/i.exec(html);
  if (head) return html.slice(0, head.index + head[0].length) + POLICY_META + html.slice(head.index + head[0].length);
  const root = /<html(\s[^>]*)?>/i.exec(html);
  if (root) return `${html.slice(0, root.index + root[0].length)}<head>${POLICY_META}</head>${html.slice(root.index + root[0].length)}`;
  const doctype = /^\s*<!doctype[^>]*>/i.exec(html);
  if (doctype) return `${doctype[0]}<head>${POLICY_META}</head>${html.slice(doctype[0].length)}`;
  return `<head>${POLICY_META}</head>${html}`;
}

/** An SVG with its tokens in a style element just inside its root. */
function svgWithTokens(svg: string, tokens: string): string {
  const open = /<svg[\s>][^>]*>|<svg>/i.exec(svg);
  if (!open) return svg;
  const at = open.index + open[0].length;
  const css = tokens.replace(/</g, '').replace(/]]>/g, '');
  return `${svg.slice(0, at)}<style><![CDATA[${css}]]></style>${svg.slice(at)}`;
}

/** The artifact made to stand alone, as `exportable` says it can be. */
export async function standaloneFile(kind: ArtifactKind, title: string, content: string, tokens: string | null): Promise<ArtifactFile> {
  if (kind === 'svg') return { name: fileName(title, 'svg'), type: 'image/svg+xml', body: tokens ? svgWithTokens(content, tokens) : content };
  if (kind === 'react') {
    const { compileReact, reactDocument } = await import('./artifactReact');
    return { name: fileName(title, 'html'), type: 'text/html', body: withPolicy(reactDocument(await compileReact(content), tokens)) };
  }
  if (kind === 'html') return { name: fileName(title, 'html'), type: 'text/html', body: withPolicy(withTokens(content, tokens)) };
  return sourceFile(kind, title, content);
}

/** Hands the file to the browser to save. */
export function saveFile(file: ArtifactFile): void {
  const url = URL.createObjectURL(new Blob([file.body], { type: `${file.type};charset=utf-8` }));
  const a = document.createElement('a');
  a.href = url;
  a.download = file.name;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
